import React, { useState } from 'react';
import { X, CreditCard, Lock } from 'lucide-react';
import axios from 'axios';
import './DonationModal.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DonationModal = ({ isOpen, onClose }) => {
  const [selectedValue, setSelectedValue] = useState(null);
  const [customValue, setCustomValue] = useState('');
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: ''
  });
  const [visibility, setVisibility] = useState('publico');
  const [wantsReceipt, setWantsReceipt] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    // Validate
    if (!selectedValue && !customValue) {
      setError('Por favor, selecione um valor para o donativo.');
      setLoading(false);
      return;
    }

    const amount = selectedValue === 'outro' ? parseFloat(customValue) : parseFloat(selectedValue);
    if (isNaN(amount) || amount < 1) {
      setError('O valor mínimo para donativo é 1€.');
      setLoading(false);
      return;
    }
    
    try {
      // Create Stripe checkout session
      const response = await axios.post(`${API}/payments/checkout/donation`, {
        package_id: selectedValue !== 'outro' ? selectedValue : null,
        custom_amount: selectedValue === 'outro' ? amount : null,
        origin_url: window.location.origin,
        donor_name: formData.nome,
        donor_email: formData.email,
        donor_phone: formData.telefone,
        visibility: visibility,
        wants_receipt: wantsReceipt
      });
      
      // Redirect to Stripe Checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('URL de pagamento não recebida');
      }
    } catch (err) {
      console.error('Erro ao processar donativo:', err);
      setError(err.response?.data?.detail || 'Ocorreu um erro ao processar o donativo. Por favor, tente novamente.');
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="donation-modal-overlay" onClick={onClose}>
      <div className="donation-modal" onClick={(e) => e.stopPropagation()}>
        <div className="donation-header">
          <div>
            <h2>Donativos AASPS</h2>
            <p>Ajude-nos a apoiar quem mais precisa</p>
          </div>
          <button className="modal-close-btn" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="donation-form">
          <div className="form-section">
            <label className="section-label">Qual o valor que pretende doar? *</label>
            <div className="value-options">
              <button
                type="button"
                className={`value-btn ${selectedValue === '5' ? 'selected' : ''}`}
                onClick={() => { setSelectedValue('5'); setCustomValue(''); }}
              >
                5€
              </button>
              <button
                type="button"
                className={`value-btn ${selectedValue === '10' ? 'selected' : ''}`}
                onClick={() => { setSelectedValue('10'); setCustomValue(''); }}
              >
                10€
              </button>
              <button
                type="button"
                className={`value-btn ${selectedValue === '15' ? 'selected' : ''}`}
                onClick={() => { setSelectedValue('15'); setCustomValue(''); }}
              >
                15€
              </button>
              <button
                type="button"
                className={`value-btn ${selectedValue === '25' ? 'selected' : ''}`}
                onClick={() => { setSelectedValue('25'); setCustomValue(''); }}
              >
                25€
              </button>
              <button
                type="button"
                className={`value-btn ${selectedValue === '50' ? 'selected' : ''}`}
                onClick={() => { setSelectedValue('50'); setCustomValue(''); }}
              >
                50€
              </button>
              <button
                type="button"
                className={`value-btn outro ${selectedValue === 'outro' ? 'selected' : ''}`}
                onClick={() => setSelectedValue('outro')}
              >
                {selectedValue === 'outro' ? (
                  <input
                    type="number"
                    placeholder="Valor €"
                    value={customValue}
                    onChange={(e) => setCustomValue(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    min="1"
                    step="0.01"
                  />
                ) : (
                  'Outro valor'
                )}
              </button>
            </div>
          </div>

          <div className="form-section">
            <label className="section-label">Dados Pessoais</label>
            <div className="personal-data">
              <input
                type="text"
                name="nome"
                placeholder="Nome completo *"
                value={formData.nome}
                onChange={handleChange}
                required
              />
              <div className="row-inputs">
                <input
                  type="email"
                  name="email"
                  placeholder="E-mail *"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
                <input
                  type="tel"
                  name="telefone"
                  placeholder="Telefone *"
                  value={formData.telefone}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <label className="section-label">Pretende que o seu donativo seja público ou anónimo?</label>
            <div className="visibility-options">
              <label className="radio-option">
                <input
                  type="radio"
                  name="visibility"
                  value="publico"
                  checked={visibility === 'publico'}
                  onChange={() => setVisibility('publico')}
                />
                <span>Público (será publicado o seu nome e valor)</span>
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  name="visibility"
                  value="anonimo"
                  checked={visibility === 'anonimo'}
                  onChange={() => setVisibility('anonimo')}
                />
                <span>Anónimo (apenas será publicado o valor)</span>
              </label>
            </div>
          </div>

          <div className="form-section">
            <label className="checkbox-option">
              <input
                type="checkbox"
                checked={wantsReceipt}
                onChange={(e) => setWantsReceipt(e.target.checked)}
              />
              <span>Desejo receber recibo de pagamento</span>
            </label>
          </div>

          {error && <div className="donation-error">{error}</div>}

          <div className="donation-actions">
            <button type="button" className="btn-fechar" onClick={onClose}>
              FECHAR
            </button>
            <button type="submit" className="btn-doar" disabled={loading}>
              {loading ? (
                'A REDIRECIONAR...'
              ) : (
                <>
                  <CreditCard size={18} />
                  PAGAR COM CARTÃO
                </>
              )}
            </button>
          </div>

          <div className="stripe-badge">
            <Lock size={14} />
            <span>Pagamento seguro processado por <strong>Stripe</strong></span>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DonationModal;
